import React, { useEffect, useState } from "react";
import { MDBBtn, MDBBtnGroup, MDBCard, MDBCardBody, MDBCardHeader, MDBCardTitle, MDBCheckbox, MDBCol, MDBIcon, MDBInput, MDBRadio, MDBRow, MDBSpinner, MDBTextArea } from "mdb-react-ui-kit";
import ApiService from "../../Core/Service/ApiService";
import { useNavigate, useParams } from "react-router-dom";
import { LondonDate, capitalizeFirstLetter, getImageUrl, validateForm } from "../../Utils/utils";
import { toast } from "react-toastify";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import { CmsType } from "../../Constants/constants";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";
import CategorySelect from "../../Components/CMS/CategorySelect";
import Ribbon from "../../Components/CMS/Ribbon";
import Media from "../../Components/CMS/Media";
import BrandSelect from "../../Components/CMS/BrandSelect";

const CmsItemEdit = React.memo(props => {
    const navigate = useNavigate();
    const cmsContext = useContext(CmsContext);
    const { currMainMenu, currSubMenu } = cmsContext;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const now = LondonDate(new Date());
    const params = useParams();
    const { cms_id, action } = params;
    
    const queryString = window.location.search;
    const searchParams = new URLSearchParams(queryString);
    const type = searchParams.get('type');

    const [formData, setFormData] = useState({
        type,
        cms_id,
        active: true,
        internal_name: '',
        ga_id: '',
        sequence: 1,
        dwell_time: 3,
        link_url: '',
        prod_codes: '',
        media_web: {
            type: 'image',
            upload_file: '',
            external_link: '',
            active_link: 'external'
        },
        media_mobile: {
            type: 'image',
            upload_file: '',
            external_link: '',
            active_link: 'external'
        },
        ribbon: {
            content: '',
            bg_color: '#fff',
            txt_color: '#f00',
            align: 'left',
        },
        data: {}
    });
    let rules = {
        sequence: {
            required: 'number'
        },
    }
    const [inputErrors, setInputErrors] = useState({});

    let hiddenFields = [];
    switch (type) {
        case CmsType.Top_Ribbon:
            hiddenFields = ['media', 'prod_codes', 'sequence'];
            break;
        case CmsType.Home_Banner:
            break;
        case CmsType.Category_Carousel:
            hiddenFields = [''];
            break;
        case CmsType.Category_Banner:
            break;
        case CmsType.Products_Carousel:
            hiddenFields = ['media', 'link_url'];
            break;
        case CmsType.Brochure:
            hiddenFields = [''];
            break;
        case CmsType.Bottom_Banner:
            hiddenFields = [];
            break;
        case CmsType.Brand:
            break;
        case CmsType.MultiBuy:
            hiddenFields = ['link_url', 'prod_codes', 'ribbon'];
            break;
    }

    useEffect(() => {
        cmsContext.setBreadcrumb([
            { title: currMainMenu?.label ?? ''},
            { title: currSubMenu?.label ?? ''},
            { title: 'Items '},
            { title: capitalizeFirstLetter(params.action) },
        ])
    }, [currMainMenu, currSubMenu])

    const isFieldHidden = (fieldName) => {
        return hiddenFields && hiddenFields.indexOf(fieldName) != -1;
    }

    const loadData = async () => {
        setLoading(true);

        let response;
        if (params.action == 'edit' || params.action == 'copy' || params.action == 'view') {
            response = await ApiService.get(`/cms_items/${params.id}?type=${type}`);
            const row = response.data.row;
            delete row.id;          // To delete id field
            delete row.cms_item_id;

            let cms_data = {
                ...formData,
                ...row,
                active: (row.active == "1") ? true : false,
                sequence: row.sequence ? parseInt(row.sequence) : formData.sequence,
                dwell_time: row.dwell_time ? parseInt(row.dwell_time) : formData.dwell_time,
                ribbon: row.ribbon ? JSON.parse(row.ribbon) : {},
                media_web: row.media_web ? JSON.parse(row.media_web) : formData.media_web,
                media_mobile: row.media_mobile ? JSON.parse(row.media_mobile) : formData.media_mobile,
                data: row.data ? JSON.parse(row.data) : formData.data,
            }

            setFormData({ ...cms_data });
        } else {
            response = await ApiService.get(`/cms_items/${cms_id}/last_sequence`);
            setFormData({
                ...formData,
                sequence: response.data.last_sequence + 1
            })
        }

        setLoading(false);
    }

    useEffect(() => {
        loadData();
    }, [params.action]);

    useEffect(() => {
        const ret = validateForm(formData, rules);
        setInputErrors(ret.errors);
    }, [formData]);

    const handleInputChange = (e) => {
        const value = e.target.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
        setFormData({
            ...formData,
            [e.target.name]: value
        })
    }
    const handleSelectChange = (opt, target) => {
        setFormData({
            ...formData,
            [target]: opt.value,
        })
    }
    const handleDateChange = (d, target) => {
        setFormData({
            ...formData,
            [target]: d
        })
    }
    const handleCheckChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.checked
        })
    }
    const setRibbon = (ribbon) => {
        setFormData({
            ...formData,
            ribbon
        })
    }

    const handleSave = async () => {
        const ret = validateForm(formData, rules);
        if (ret.result) {
            setInputErrors({});
            setSaving(true);

            const data = {
                ...formData,
                prod_codes: formData.prod_codes.split(',').map(pc => pc.trim()).join(','),
                ribbon: JSON.stringify({
                    bg_color: '#f00',
                    txt_color: '#fff',
                    align: 'left',
                    ...formData.ribbon
                }),
                media_web: JSON.stringify(formData.media_web),
                media_mobile: JSON.stringify(formData.media_mobile),
                data: JSON.stringify(formData.data ?? {})
            }

            if (type == CmsType.Sponsor) {
                data.terms = data.terms.split(',').map(t => t.trim()).join(',')
            }

            if(params.action == 'edit') {
                await ApiService.put(`/cms_items/${params.id}`, data);
            } else {
                await ApiService.post(`/cms_items`, data)
            }

            setSaving(false);
            goBack();
        } else {
            setInputErrors(ret.errors);
        }
    }

    const goBack = () => {
        navigate(-1);
    }

    const renderCommon = () => {
        return (
        <MDBCard>
            <MDBCardHeader>
                <MDBCardTitle>Common</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                <div className="d-flex">
                    {!isFieldHidden('sequence') && (
                        <div className="me-4" title="The sequence in which CMS content is displayed">
                            <label className="required">Sequence:</label>
                            <div style={{ width: 100 }}>
                                <MDBInput name="sequence" value={formData.sequence} onChange={handleInputChange} type="number" className="text-center" />
                            </div>
                            {inputErrors?.sequence && <div className="error">{inputErrors.sequence}</div>}
                        </div>
                    )}

                    <div className="me-4" title="Dwell time in seconds for each item">
                        <label>Dwell Time:</label>
                        <div style={{ width: 100 }}>
                            <MDBInput name="dwell_time" value={formData.dwell_time} onChange={handleInputChange} type="number" className="text-center" min="1" />
                        </div>
                        {inputErrors?.dwell_time && <div className="error">{inputErrors.dwell_time}</div>}
                    </div>

                    <div className="me-4" title='Internal name for CMS content'>
                        <label>Internal Name:</label>
                        <MDBInput name="internal_name" value={formData.internal_name} onChange={handleInputChange} />
                    </div>

                    <div className="me-4">
                        <label>GA Id:</label>
                        <div>
                            <MDBInput name="ga_id" value={formData.ga_id} onChange={handleInputChange} />
                        </div>
                        {inputErrors?.ga_id && <div className="error">{inputErrors.ga_id}</div>}
                    </div>

                    <div className="me-4">
                        <label>Active:</label>
                        <MDBCheckbox name="active" checked={formData.active} onChange={handleCheckChange} title='Activate/Deactivate the CMS content' />
                    </div>
                </div>
            </MDBCardBody>
        </MDBCard>
        )
    }

    const renderLink = () => {
        return (
        <MDBCard className="mt-4">
            <MDBCardHeader>
                <MDBCardTitle>Link</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                {!isFieldHidden('link_url') && (
                    <div className="">
                        <label>Link Url:</label>
                        <MDBInput name="link_url" value={formData.link_url} onChange={handleInputChange} />
                        {inputErrors?.link_url && <div className="error">{inputErrors.link_url}</div>}
                    </div>
                )}

                {!isFieldHidden('prod_codes') && (
                    <div className="mt-4">
                        <label>Product Codes:</label>
                        <MDBInput name="prod_codes" value={formData.prod_codes} onChange={handleInputChange} />
                        {inputErrors?.prod_codes && <div className="error">{inputErrors.prod_codes}</div>}
                    </div>
                )}
            </MDBCardBody>
        </MDBCard>
        )
    }

    const renderBasicEdit = () => (
        <div className="py-4">
            { renderCommon() }

            { renderLink() }

            { !isFieldHidden('ribbon') && (
                <Ribbon ribbon={formData.ribbon} setRibbon={setRibbon} inputErrors={inputErrors} />
            ) }

            { !isFieldHidden('media') && (
                <Media formData={formData} setFormData={setFormData} webField='media_web' mobileField='media_mobile' /> 
            )}
        </div>
    )

    const renderCustomFields = () => {
        const renderCategoryCarousel = () => (
            <MDBCard>
                <MDBCardBody>
                    <CategorySelect data={{
                            top_cat_id: formData.data?.top_cat_id ?? '',
                            sub_cat_id: formData.data?.sub_cat_id ?? ''
                        }} 
                        setData={({top_cat_id, sub_cat_id}) => {
                            setFormData({
                                ...formData,
                                data: {
                                    ...formData.data,
                                    top_cat_id, 
                                    sub_cat_id
                                }
                            })
                        }} 
                    />
                </MDBCardBody>
            </MDBCard>
        )
        const renderBrand = () => (
            <MDBCard>
                <MDBCardBody>
                    <BrandSelect 
                        data={formData.data.brand} 
                        setData={(brand) => setFormData({
                            ...formData,
                            data: {
                                ...formData.data,
                                brand
                            }
                        })} 
                    />
                </MDBCardBody>
            </MDBCard>
        )
        const renderSponsor = () => (
            <MDBCard>
                <MDBCardBody>

                </MDBCardBody>
            </MDBCard>
        )

        switch(type) {
            case CmsType.Category_Carousel:
                return renderCategoryCarousel();
            case CmsType.Brand:
                return renderBrand();
            case CmsType.Sponsor:
                return renderSponsor();
        }
    }

    return (<section className={`px-4 pb-4`}>
        <div className={`${params.action == 'view' ? 'disabled-opacity-1' : ''}`}>
            {renderBasicEdit()}

            {renderCustomFields()}
        </div>

        <div className="d-flex justify-content-end mt-4">
            <MDBBtn color="default" onClick={goBack}>Back</MDBBtn>
            {params.action != 'view' && (
                <MDBBtn className="ms-2 united-cms-primary-btn" onClick={handleSave}>
                    {saving && <MDBSpinner size="sm" />}
                    <span className="ms-1">Save</span>
                </MDBBtn>
            )}
        </div>

        <LoadingIndicator show={loading} />
    </section>)
})

export default CmsItemEdit;